import { FC, ReactNode } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { colors, radii } from '../../theme/tokens';
import ListState, { ListStateProps, hasListState } from '../ListState';

// The table of the XD "Common structure", on desktop and tablet: orange
// header (the theme sets it in MuiTableHead / MuiTableCell), white rows
// separated by a thin line, an orange rule above and below the row on
// hover, and a final actions column whose "•••" expands into icons
// on mouse-over (see components/RowActions).
//
// It is MUI's <Table> and not a DataGrid, so that the design is exactly that.
// The pagination is painted by the page (ListPagination), outside the table.
//
// The rows are supplied by each listing as `children`; when there is a state
// to show (loading, error, empty) it is painted in their place.

type Props = ListStateProps & {
  ariaLabel: string;
  columns: readonly string[];
  /** Width reserved for the actions column (see `rowActionsWidth`). */
  actionsWidth: number;
  minWidth?: number;
  children: ReactNode;
};

// Header of the actions column: no text in the XD, but screen readers
// need one.
export const VISUALLY_HIDDEN_SX = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const HOVER_FILET = `inset 0 2px 0 ${colors.accent}, inset 0 -2px 0 ${colors.accent}`;

const ListTable: FC<Props> = ({
  ariaLabel,
  columns,
  actionsWidth,
  minWidth = 720,
  children,
  ...state
}) => (
  <TableContainer sx={{ borderRadius: `${radii.card}px` }}>
    <Table aria-label={ariaLabel} sx={{ minWidth }}>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column}>{column}</TableCell>
          ))}
          <TableCell align="right" sx={{ width: actionsWidth + 16 }}>
            <Box component="span" sx={VISUALLY_HIDDEN_SX}>
              Actions
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody
        sx={{
          '& .MuiTableRow-root': {
            backgroundColor: colors.white,
          },
          '& .MuiTableCell-body': {
            // XD: the rule between rows is orange, and the table has no
            // outer grey border.
            borderBottom: `1px solid ${colors.accent}`,
            color: colors.textBase,
            py: 1,
          },
          // Orange rule above and below the row on hover, as an inner
          // shadow of each cell so as not to alter the row height.
          '& .MuiTableRow-root:hover .MuiTableCell-body': {
            boxShadow: HOVER_FILET,
          },
          '& .MuiTableRow-root:hover .RowActions-icons': {
            visibility: 'visible',
          },
          '& .MuiTableRow-root:hover .RowActions-rest': {
            visibility: 'hidden',
          },
        }}
      >
        {hasListState(state) ? (
          <TableRow>
            <TableCell colSpan={columns.length + 1} sx={{ border: 0 }}>
              <ListState {...state} />
            </TableCell>
          </TableRow>
        ) : (
          children
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default ListTable;
